import { nextTick } from 'vue';
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
  START_LOCATION,
} from 'vue-router';
import type { AdminRole } from '@/types';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    roles?: AdminRole[];
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false, title: 'Sign In' },
  },
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    // No `roles` — every authenticated staff admin manages their own account.
    meta: { requiresAuth: true, title: 'My Account' },
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Overview' },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/users/UsersView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Members',
    },
  },
  {
    path: '/users/:id',
    name: 'user-detail',
    component: () => import('@/views/users/UserDetailView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Member Detail',
    },
  },
  {
    path: '/albums',
    name: 'albums',
    component: () => import('@/views/albums/AlbumsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR', 'SUPPORT'],
      title: 'Albums',
    },
  },
  {
    path: '/albums/:id',
    name: 'album-detail',
    component: () => import('@/views/albums/AlbumDetailView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR', 'SUPPORT'],
      title: 'Album Detail',
    },
  },
  {
    path: '/albums/:id/access-requests',
    name: 'album-access-requests',
    component: () => import('@/views/albums/AccessRequestsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT', 'MODERATOR'],
      title: 'Access Requests',
    },
  },
  {
    path: '/themes',
    name: 'themes',
    component: () => import('@/views/ThemesView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Themes' },
  },
  {
    path: '/themes/new',
    name: 'theme-create',
    component: () => import('@/views/ThemeEditorView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Create Theme' },
  },
  {
    path: '/themes/:id',
    name: 'theme-edit',
    component: () => import('@/views/ThemeEditorView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Edit Theme' },
  },
  {
    path: '/circles',
    name: 'circles',
    component: () => import('@/views/circles/CirclesView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Circles',
    },
  },
  {
    // Its own route root, not `/circles/...`, because the pool-number surface
    // is a SEPARATE controller in the API whose GET is deliberately NOT a
    // SUPPORT surface — every route on `/v1/admin/circle-pool-numbers` is
    // SUPER_ADMIN|ADMIN, so this entry carries those two roles and nothing
    // else. Keeping it off `/circles/:circleId` also keeps it clear of that
    // param route.
    path: '/circle-pool-numbers',
    name: 'circle-pool-numbers',
    component: () => import('@/views/circles/PoolNumbersView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN'],
      title: 'Circle Numbers',
    },
  },
  {
    path: '/circles/:circleId',
    name: 'circle-detail',
    component: () => import('@/views/circles/CircleDetailView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Circle Detail',
    },
  },
  {
    path: '/event-types',
    name: 'event-types',
    component: () => import('@/views/event-types/EventTypesView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Event Types' },
  },
  {
    path: '/products',
    name: 'products',
    component: () => import('@/views/products/ProductsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
      title: 'Products',
    },
  },
  {
    path: '/domains',
    name: 'domains',
    component: () => import('@/views/AllowlistedDomainsView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Allowlisted Domains' },
  },
  {
    path: '/runtime-settings',
    name: 'runtime-settings',
    component: () => import('@/views/RuntimeSettingsView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'], title: 'Runtime Settings' },
  },
  {
    path: '/queues',
    name: 'queues',
    component: () => import('@/views/QueuesView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'], title: 'Queues' },
  },
  {
    path: '/ops',
    name: 'ops',
    component: () => import('@/views/OpsView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN'], title: 'Cron Triggers' },
  },
  {
    path: '/voice-greetings',
    name: 'voice-greetings',
    component: () => import('@/views/VoiceGreetingsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Voice Greetings',
    },
  },
  {
    path: '/moderation',
    name: 'moderation',
    component: () => import('@/views/moderation/ModerationView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'],
      title: 'Moderation Queue',
    },
  },
  {
    path: '/content',
    name: 'content',
    component: () => import('@/views/content/ContentView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'], title: 'Content' },
  },
  {
    path: '/payments',
    name: 'payments',
    component: () => import('@/views/payments/PaymentsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
      title: 'Payments & Subscriptions',
    },
  },
  {
    path: '/discount-codes',
    name: 'discount-codes',
    component: () => import('@/views/discount-codes/DiscountCodesView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
      title: 'Discount Codes',
    },
  },
  {
    path: '/discount-codes/campaigns',
    name: 'discount-campaigns',
    component: () => import('@/views/discount-codes/DiscountCampaignsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
      title: 'Discount Campaigns',
    },
  },
  {
    path: '/discount-codes/campaigns/:id',
    name: 'discount-campaign-detail',
    component: () => import('@/views/discount-codes/DiscountCampaignDetailView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
      title: 'Campaign Detail',
    },
  },
  {
    path: '/credits/campaigns',
    name: 'admin-credit-campaigns',
    component: () => import('@/views/credits/CampaignsListView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN'],
      title: 'Credit Campaigns',
    },
  },
  {
    path: '/credits/campaigns/:creditCampaignId',
    name: 'admin-credit-campaign-detail',
    component: () => import('@/views/credits/CampaignDetailView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN'],
      title: 'Credit Campaign Detail',
    },
  },
  {
    path: '/referrals',
    name: 'referrals',
    component: () => import('@/views/referrals/ReferralsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'REPORTING'],
      title: 'Referrals',
    },
  },
  {
    path: '/short-links',
    name: 'short-links',
    component: () => import('@/views/short-links/ShortLinksView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Short Links',
    },
  },
  {
    path: '/issues',
    name: 'issues',
    component: () => import('@/views/issues/IssuesView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Bugs & Feedback',
    },
  },
  // Legacy paths now live under the merged Bugs & Feedback view.
  { path: '/bug-reports', redirect: '/issues' },
  { path: '/feedback', redirect: '/issues' },
  {
    path: '/delivery',
    name: 'delivery',
    component: () => import('@/views/delivery/DeliveryView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Email & SMS Delivery',
    },
  },
  {
    path: '/invitations',
    name: 'invitations',
    component: () => import('@/views/invitations/InvitationsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'],
      title: 'Invitation Abuse',
    },
  },
  {
    path: '/suppressions',
    name: 'suppressions',
    component: () => import('@/views/suppressions/SuppressionsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Email Suppressions',
    },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/views/audit/AuditLogView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN'],
      title: 'Audit Log',
    },
  },
  {
    path: '/admins',
    name: 'admins',
    component: () => import('@/views/admins/AdminsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN'],
      title: 'Admin Users',
    },
  },
  {
    path: '/beta',
    name: 'beta',
    component: () => import('@/views/beta/BetaView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Beta Invites',
    },
  },
  {
    path: '/safety',
    name: 'safety',
    component: () => import('@/views/safety/SafetyView.vue'),
    meta: { requiresAuth: true, roles: ['SUPER_ADMIN', 'ADMIN'], title: 'Safety' },
  },
  {
    path: '/csam',
    name: 'csam',
    component: () => import('@/views/csam/CsamCasesView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'CSAM Cases',
    },
  },
  {
    path: '/media-lookup',
    name: 'media-lookup',
    component: () => import('@/views/media/MediaLookupView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Media Lookup',
    },
  },
  {
    path: '/reporting',
    name: 'reporting',
    component: () => import('@/views/reporting/ReportingView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'REPORTING', 'FINANCE'],
      title: 'Reporting',
    },
  },
  {
    path: '/ad-attribution',
    name: 'ad-attribution',
    component: () => import('@/views/reporting/AdAttributionView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'REPORTING', 'FINANCE'],
      title: 'Ad Attribution',
    },
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/help/HelpView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Help Center',
    },
  },
  {
    path: '/help/articles/:id',
    name: 'help-article-editor',
    component: () => import('@/views/help/HelpArticleEditor.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
      title: 'Help Article Editor',
    },
  },
  {
    path: '/lifecycle',
    name: 'lifecycle',
    component: () => import('@/views/lifecycle/LifecycleCenterView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'REPORTING'],
      title: 'Lifecycle Center',
    },
  },
  {
    path: '/lifecycle/:keyCd',
    name: 'lifecycle-detail',
    component: () => import('@/views/lifecycle/LifecycleCenterView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['SUPER_ADMIN', 'ADMIN', 'REPORTING'],
      title: 'Lifecycle Center',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { requiresAuth: false, title: '404 — Not Found' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0 };
  },
});

// ---------------------------------------------------------------------------
// Navigation guard
// ---------------------------------------------------------------------------
let authInitialized = false;

router.beforeEach(async (to, _from) => {
  // Lazy-import store to avoid circular dependency at module load
  const { useAuthStore } = await import('@/stores/auth');
  const authStore = useAuthStore();

  // On first navigation, try to restore session from httpOnly refresh cookie.
  // This runs for /login too so a user with a live session who lands directly
  // on the login page (bookmark, address bar) is redirected to their home
  // route instead of being asked to sign in again.
  if (!authInitialized) {
    authInitialized = true;
    await authStore.initAuth();
  }

  const requiresAuth = to.meta.requiresAuth !== false;

  // If route requires auth and user has no token → redirect to login
  if (requiresAuth && !authStore.isAuthenticated) {
    sessionStorage.setItem('admin_intended_route', to.fullPath);
    return { name: 'login' };
  }

  // If already authenticated and heading to login → redirect to role home
  if (to.name === 'login' && authStore.isAuthenticated) {
    return authStore.homeRoute;
  }

  // If authenticated but adminUser not yet loaded, try to fetch it
  if (authStore.isAuthenticated && !authStore.adminUser) {
    try {
      await authStore.fetchMe();
    } catch {
      // Token is invalid — clear and redirect
      authStore.accessTokenTx = null;
      sessionStorage.setItem('admin_intended_route', to.fullPath);
      return { name: 'login' };
    }
  }

  // Role check — if route specifies required roles, verify user has access
  const requiredRoles = to.meta.roles as AdminRole[] | undefined;
  if (requiredRoles && requiredRoles.length > 0 && authStore.adminUser) {
    if (!authStore.canAccess(requiredRoles)) {
      const home = authStore.homeRoute;
      // Avoid a redirect loop if the user is already heading to their home.
      return to.path === home ? true : home;
    }
  }

  return true;
});

// ---------------------------------------------------------------------------
// Page title and focus after navigation (WCAG 2.4.2 Page Titled, 2.4.3)
// ---------------------------------------------------------------------------
const APP_TITLE = 'ShareLocket Admin';

/** The view a location renders: its deepest match's (resolved) component. */
function viewOf(location: RouteLocationNormalized) {
  return location.matched[location.matched.length - 1]?.components?.default;
}

router.afterEach((to, from, failure) => {
  if (failure) return;
  document.title = to.meta.title ? `${to.meta.title} | ${APP_TITLE}` : APP_TITLE;

  // A first load leaves focus at the top of the document, where the skip link
  // is the first stop. Within one view (a query or hash change, the skip
  // link's own #main-content, a deep-linked drawer such as /lifecycle/:keyCd)
  // the view owns focus, and moving it would yank it out of a tab or drawer.
  if (from === START_LOCATION || viewOf(to) === viewOf(from)) return;

  // A new view: put focus on its heading, so a screen reader announces the
  // page, and the next Tab continues from the page, not the old sidebar link.
  // A view whose h1 waits on data falls back to the main region.
  void nextTick(() => {
    const main = document.querySelector<HTMLElement>('main');
    const target = main?.querySelector<HTMLElement>('h1') ?? main;
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
});

export default router;
